import { useState } from "react";

import { useNavigate } from "react-router-dom";

import { ArrowLeft, Edit, Plus, Trash } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { Switch } from "~/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  useAssemblyLines,
  useCreateAssemblyLine,
  useDeleteAssemblyLine,
  useUpdateAssemblyLine,
} from "~/hooks/production";
import { useToast } from "~/hooks/use-toast";
import type { AssemblyLine, AssemblyLineInput } from "~/types/production";

const NO_PLANT = "none";

interface FormState {
  code: string;
  name: string;
  plant: string;
  sort_order: number;
  is_active: boolean;
}

const emptyForm: FormState = {
  code: "",
  name: "",
  plant: NO_PLANT,
  sort_order: 0,
  is_active: true,
};

export function AssemblyLinesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: lines = [], isLoading } = useAssemblyLines(true);
  const createMutation = useCreateAssemblyLine();
  const updateMutation = useUpdateAssemblyLine();
  const deleteMutation = useDeleteAssemblyLine();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<AssemblyLine | null>(null);
  const [deletingLine, setDeletingLine] = useState<AssemblyLine | null>(null);
  const [formData, setFormData] = useState<FormState>(emptyForm);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingLine(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (line: AssemblyLine) => {
    setEditingLine(line);
    setFormData({
      code: line.code,
      name: line.name ?? "",
      plant: line.plant ?? NO_PLANT,
      sort_order: line.sort_order,
      is_active: line.is_active,
    });
    setIsDialogOpen(true);
  };

  // Auto-suggest name/sort_order from the code while creating (e.g. "L17" -> "Línea 17", 17).
  const handleCodeChange = (raw: string) => {
    const code = raw.toUpperCase().replace(/\s/g, "");
    const digits = code.replace(/\D/g, "");
    setFormData((prev) => ({
      ...prev,
      code,
      name: prev.name ? prev.name : digits ? `Línea ${digits}` : prev.name,
      sort_order:
        prev.sort_order && prev.sort_order !== 0
          ? prev.sort_order
          : digits
            ? Number(digits)
            : prev.sort_order,
    }));
  };

  const describeError = (error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    if (/duplicate key|unique constraint|already exists/i.test(message)) {
      return "Ya existe una línea con ese código.";
    }
    return message;
  };

  const handleSubmit = async () => {
    const code = formData.code.trim().toUpperCase();
    if (!code) {
      toast({
        title: "Error",
        description: "El código es obligatorio",
        variant: "destructive",
      });
      return;
    }

    const payload: AssemblyLineInput = {
      code,
      name: formData.name.trim() || null,
      plant: formData.plant === NO_PLANT ? null : formData.plant,
      is_active: formData.is_active,
      sort_order: formData.sort_order || 0,
    };

    try {
      if (editingLine) {
        await updateMutation.mutateAsync({
          id: editingLine.id,
          input: payload,
        });
        toast({ title: "Línea actualizada", description: `${code} guardada` });
      } else {
        await createMutation.mutateAsync(payload);
        toast({ title: "Línea creada", description: `${code} agregada` });
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: describeError(error),
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (
    line: AssemblyLine,
    nextActive: boolean
  ) => {
    try {
      await updateMutation.mutateAsync({
        id: line.id,
        input: { is_active: nextActive },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: describeError(error),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!deletingLine) return;
    try {
      await deleteMutation.mutateAsync(deletingLine.id);
      toast({
        title: "Línea eliminada",
        description: `${deletingLine.code} eliminada`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: describeError(error),
        variant: "destructive",
      });
    } finally {
      setDeletingLine(null);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <PageContainer>
      <PageHeader
        title="Líneas de Envasado"
        description="Administre las líneas disponibles al crear lotes de producción"
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/production/batches")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button onClick={openCreateDialog} className="h-9">
            <Plus className="mr-2 h-4 w-4" />
            Nueva Línea
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32 whitespace-nowrap">
                    Código
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Nombre</TableHead>
                  <TableHead className="w-28 whitespace-nowrap">
                    Planta
                  </TableHead>
                  <TableHead className="w-24 whitespace-nowrap">
                    Orden
                  </TableHead>
                  <TableHead className="w-28 whitespace-nowrap">
                    Activa
                  </TableHead>
                  <TableHead className="w-24 whitespace-nowrap text-right">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={6}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : lines.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No hay líneas de envasado definidas
                    </TableCell>
                  </TableRow>
                ) : (
                  lines.map((line, index) => (
                    <TableRow
                      key={line.id}
                      className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${line.is_active ? "" : "opacity-60"}`}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                    >
                      <TableCell className="whitespace-nowrap font-medium">
                        {line.code}
                      </TableCell>
                      <TableCell>{line.name || "—"}</TableCell>
                      <TableCell className="whitespace-nowrap">
                        {line.plant || "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {line.sort_order}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={line.is_active}
                          onCheckedChange={(checked) =>
                            handleToggleActive(line, checked)
                          }
                          aria-label={`Activar línea ${line.code}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(line)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingLine(line)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingLine ? "Editar Línea" : "Nueva Línea"}
            </DialogTitle>
            <DialogDescription>
              El código identifica la línea en los lotes (p. ej. L17).
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Código *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="L17"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sort_order">Orden</Label>
                <Input
                  id="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sort_order: Number(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Línea 17"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="plant">Planta por defecto</Label>
              <Select
                value={formData.plant}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, plant: value }))
                }
              >
                <SelectTrigger id="plant">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PLANT}>Ninguna</SelectItem>
                  <SelectItem value="P1">Planta 1 (P1)</SelectItem>
                  <SelectItem value="P2">Planta 2 (P2)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="is_active" className="font-normal">
                  Línea activa
                </Label>
                <p className="text-xs text-muted-foreground">
                  Solo las líneas activas aparecen al crear lotes
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, is_active: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={isSaving}>
              {editingLine ? "Guardar Cambios" : "Crear Línea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingLine}
        onOpenChange={(open) => !open && setDeletingLine(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Línea</DialogTitle>
            <DialogDescription>
              ¿Eliminar la línea{" "}
              <span className="font-medium">{deletingLine?.code}</span>? Si ya
              fue usada en lotes, considere desactivarla en lugar de eliminarla.
              <br />
              <span className="font-medium text-destructive">
                Esta acción no se puede deshacer.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingLine(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
